import React from 'react'
import { cn } from '@/lib/utils'
import { buildMonthWeeks, currencySymbol, formatRateSym } from '@/lib/calendar-utils'
import { Ban, DoorClosed, CalendarX, CircleOff } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CalendarDayPopup from './CalendarDayPopup'
import { hasTargetAdjustment } from '@/lib/target-utils'
import type { RoomType, RateData, OccupancyDay, ComparisonMode } from '@/types/calendar'

interface TargetMarker {
  state: string
  reasonCopy: string
  targetYear: number
}

interface CalendarMonthGridProps {
  year: number
  month: number
  roomTypeId: number
  roomType?: RoomType
  rates: Record<string, RateData>
  occupancyByDate: Record<string, OccupancyDay>
  propertyOccupancyByDate?: Record<string, { booked: number; available: number; occupancyPct: number; label?: string | null }>
  comparisonData: Record<string, number | null>
  comparisonMode: ComparisonMode
  currency: string
  onCellClick: (date: string) => void
  adrData?: Record<string, { adr: number | null; lastYearAdr: number | null }>
  /** The property's backend-served canonical path — threaded to the day popup's target link. */
  propertyBasePath?: string
  targetMarkerByMonth?: Record<number, TargetMarker>
  /** Today AT THE PROPERTY (YYYY-MM-DD) — see calendar-utils#todayIso. */
  today: string
}

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Stable empty default so an unset targetMarkerByMonth prop doesn't create a
// fresh object every render (which would defeat React.memo on this grid).
const EMPTY_TARGET_MARKERS: Record<number, TargetMarker> = {}

function CalendarMonthGrid({
  year,
  month,
  roomTypeId,
  roomType,
  rates,
  occupancyByDate,
  propertyOccupancyByDate,
  comparisonData,
  comparisonMode,
  currency,
  onCellClick,
  adrData,
  propertyBasePath,
  targetMarkerByMonth = EMPTY_TARGET_MARKERS,
  today: propertyToday,
}: CalendarMonthGridProps) {
  const weeks = buildMonthWeeks(year, month)
  const sym = currencySymbol(currency)
  // Target Pressure month-level marker (dormant or at_floor)
  const targetMarker = targetMarkerByMonth[month] ?? null

  return (
    <TooltipProvider delayDuration={800} skipDelayDuration={0}>
      <div className="rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border/40">
          {DAY_HEADERS.map((day, i) => (
            <div
              key={day}
              className={cn(
                'px-2 py-2.5 text-center text-xs font-semibold uppercase tracking-wider',
                i >= 5 ? 'text-primary/70 bg-slate-50/50' : 'text-muted-foreground'
              )}
            >
              {day}
            </div>
          ))}
        </div>

        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 border-b border-border/30 last:border-b-0">
            {week.map((dateStr, dayIdx) => {
              if (!dateStr) {
                return <div key={dayIdx} className="min-h-[100px] bg-slate-50/30 border-r border-border/20 last:border-r-0" />
              }

              const dayNum = parseInt(dateStr.split('-')[2], 10)
              const today = dateStr === propertyToday
              const isPast = dateStr < propertyToday
              const weekend = dayIdx >= 5
              const dayOfWeek = DAY_HEADERS[dayIdx]

              const rateKey = `${roomTypeId}:${dateStr}`
              const rate = rates[rateKey]
              const occ = occupancyByDate[dateStr]
              const propertyOcc = propertyOccupancyByDate?.[dateStr]
              const showHotel = !!(occ && propertyOcc && propertyOcc.available !== occ.available)
              const comparison = comparisonData[rateKey]

              const baseRate = rate?.computationDetails?.adjustments?.find(
                (a) => a.stage === 'base_rate'
              )?.value
              const isAboveBase = baseRate != null && rate != null && rate.rateAmount > baseRate
              const isBelowBase = baseRate != null && rate != null && rate.rateAmount < baseRate
              const isOverride = rate?.source === 'manual_override'
              const hasTarget = hasTargetAdjustment(rate?.computationDetails?.adjustments)

              let compDiff: number | null = null
              if (comparisonMode !== 'none' && comparison != null && rate) {
                compDiff = rate.rateAmount - comparison
              }

              return (
                <div
                  key={dateStr}
                  className={cn(
                    'min-h-[100px] border-r border-border/20 last:border-r-0 p-0',
                    isPast && 'opacity-40',
                    today && 'bg-primary/5',
                    weekend && !today && !isPast && 'bg-slate-50/30',
                    isOverride && !isPast && 'ring-2 ring-inset ring-blue-400/40',
                    rate?.stopSell && 'bg-slate-100/80'
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        onClick={() => onCellClick(dateStr)}
                        className="min-h-[100px] p-2 transition-colors cursor-pointer hover:bg-muted/30"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={cn(
                            'text-xs font-bold',
                            today ? 'text-primary' : 'text-foreground'
                          )}>
                            {dayNum}
                          </span>
                          <div className="flex items-center gap-0.5">
                            {hasTarget && !rate?.stopSell && (
                              <span
                                title="Target adjusted this rate"
                                className="inline-block h-2 w-2 rounded-full bg-teal-500 opacity-80"
                              />
                            )}
                            {targetMarker && (
                              <span title={targetMarker.reasonCopy}>
                                {targetMarker.state === 'dormant' ? (
                                  <CircleOff className="h-2.5 w-2.5 text-slate-400" />
                                ) : (
                                  <span className="inline-block h-2 w-2 rounded-full bg-amber-400 opacity-70" />
                                )}
                              </span>
                            )}
                            {/* Clearance is running on this day — the depth exactly as the backend
                                stamped it (plan §7). The hover card carries the reason. */}
                            {rate?.clearanceOffsetPct != null && !rate.stopSell && (
                              <span
                                title="Clearance is lowering this price"
                                className="rounded bg-amber-100 px-1 text-[9px] font-semibold tabular-nums leading-tight text-amber-700"
                              >
                                {rate.clearanceOffsetPct}%
                              </span>
                            )}
                            {rate?.stopSell && <Ban className="h-3 w-3 text-rose-500" />}
                            {rate?.closedToArrival && <DoorClosed className="h-3 w-3 text-amber-500" />}
                            {rate?.closedToDeparture && <CalendarX className="h-3 w-3 text-amber-500" />}
                          </div>
                        </div>

                        {rate && !rate.stopSell && (
                          <>
                            <div className={cn(
                              'text-base font-bold tabular-nums leading-tight',
                              isAboveBase ? 'text-emerald-700' : isBelowBase ? 'text-rose-700' : 'text-foreground'
                            )}>
                              {formatRateSym(rate.rateAmount, sym)}
                            </div>
                            <div className={cn(
                              'flex items-center gap-1 mt-0.5',
                              rate.syncStatus === 'synced' ? 'text-emerald-600' :
                              rate.syncStatus === 'pending' ? 'text-amber-500' : 'text-muted-foreground/50'
                            )}>
                              <span className={cn(
                                'inline-block h-1.5 w-1.5 rounded-full',
                                rate.syncStatus === 'synced' ? 'bg-emerald-500' :
                                rate.syncStatus === 'pending' ? 'bg-amber-500' : 'bg-slate-300'
                              )} />
                              <span className="text-[10px] tabular-nums">
                                PMS: {rate.pushedRateAmount != null ? formatRateSym(rate.pushedRateAmount, sym) : '--'}
                              </span>
                            </div>
                          </>
                        )}
                        {rate?.stopSell && (
                          <div className="text-xs font-bold text-slate-500 uppercase">Stop</div>
                        )}

                        {compDiff != null && !rate?.stopSell && (
                          <div className={cn(
                            'text-[10px] tabular-nums mt-0.5',
                            compDiff > 0 ? 'text-emerald-600' : compDiff < 0 ? 'text-rose-600' : 'text-muted-foreground'
                          )}>
                            {compDiff > 0 ? '+' : compDiff < 0 ? '-' : ''}{formatRateSym(Math.abs(compDiff), sym)} vs {
                              comparisonMode === 'base_rate' ? 'base' :
                              comparisonMode === 'yesterday' ? 'yest.' : 'last wk'
                            }
                          </div>
                        )}

                        {occ && (
                          <div className="mt-1 flex items-center gap-1 text-[11px] tabular-nums">
                            <span className={cn(
                              'font-semibold',
                              occ.occupancyPct >= 80 ? 'text-emerald-600' :
                              occ.occupancyPct >= 60 ? 'text-amber-600' : 'text-slate-500'
                            )}>
                              {occ.booked}/{occ.available}
                            </span>
                            {showHotel && (
                              <>
                                <span className="text-muted-foreground/50">·</span>
                                {/* "building" at a combined inventory (server label), "hotel" elsewhere, the
                                    same words as the hover card beside it and the Occupancy calendar. */}
                                <span className="text-muted-foreground">
                                  {propertyOcc!.booked}/{propertyOcc!.available}{' '}
                                  <span className={cn(propertyOcc!.label && 'lowercase')}>{propertyOcc!.label ?? 'hotel'}</span>
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      sideOffset={4}
                      className="max-w-none overflow-visible border-none bg-transparent p-0 shadow-none"
                    >
                      <CalendarDayPopup
                        today={propertyToday}
                        date={dateStr}
                        dayOfWeek={dayOfWeek}
                        currency={currency}
                        rate={rate}
                        occupancy={occ ? {
                          booked: occ.booked,
                          available: occ.available,
                          occupancyPct: occ.occupancyPct,
                          pickup7d: occ.pickup7d,
                          pickup7dRooms: occ.pickup7dRooms,
                        } : undefined}
                        propertyOccupancy={propertyOccupancyByDate?.[dateStr]}
                        roomType={roomType}
                        adr={adrData?.[dateStr]?.adr}
                        lastYearAdr={adrData?.[dateStr]?.lastYearAdr}
                        propertyBasePath={propertyBasePath}
                      />
                    </TooltipContent>
                  </Tooltip>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </TooltipProvider>
  )
}

export default React.memo(CalendarMonthGrid)
